import { Component, OnInit } from '@angular/core';
import { Edit, LucideAngularModule, Plus, Trash } from 'lucide-angular';
import { RouteParams } from '@app/shared/abstract/route-params';
import { ProductService } from '@app/products/shared/product.service';
import { ProductModel } from '../shared/product.model';
import { RouterLink } from '@angular/router';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { FormControl, FormsModule } from '@angular/forms';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { debounceTime, takeUntil } from 'rxjs';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { Column } from '@app/partials/datatable/data-table/column';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ModalComponent } from '@app/partials/modal/modal.component';

@Component({
  selector: 'app-index11',
  imports: [
    LucideAngularModule,
    RouterLink,
    DataTableComponent,
    FormsModule,
    SelectComponent,
    TextInputComponent,
    ModalComponent,
  ],
  templateUrl: './products-index.component.html',
  styleUrl: './products-index.component.css',
})
export class ProductsIndexComponent extends Destroyer implements OnInit {
  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;
  protected readonly TrashIcon = Trash;

  protected productsPage?: PagedEntities<ProductModel>;
  protected categoriesControl = new FormControl();

  protected productToDelete?: ProductModel;
  protected productDeleteError?: string;

  protected categories = [
    { value: 'Dining', label: 'Dining' },
    { value: 'Beverages', label: 'Beverages' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Add-on', label: 'Add-on' },
    { value: 'Supplier', label: 'Supplier' },
    { value: 'Expense', label: 'Expense' },
    { value: 'Service', label: 'Service' },
  ];

  routeParams = new RouteParams({
    pageNum: 1,
  });

  tableColumns: Column<ProductModel>[] = [
    { field: 'id', header: 'Id' },
    { field: 'image_1_sm', header: 'Picture', type: 'image' },
    { field: 'name', header: 'Name', sortable: true },
    { field: 'category', header: 'Category' },
    { field: 'price_formatted', header: 'Price', sortable: true, sortSource: 'price', type: 'number' },
    // { field: 'in_stock', header: 'In Stock', type: 'boolean' },
    { field: 'destined_for', header: 'Destined For' },
  ];

  rowActions = [
    {
      icon: this.EditIcon,
      type: 'link',
      routerLink: (item: ProductModel) => ['/products', item.id, 'edit'],
    },
    {
      icon: this.TrashIcon,
      classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
      callback: (product: ProductModel) => {
        this.productToDelete = product;
      },
    },
  ];

  searchControl = new FormControl();

  constructor(private service: ProductService) {
    super();
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((value) => {
      this.routeParams.filter = value ? [{ field: 'search', value: value }] : [];
      if (this.categoriesControl.value) {
        this.routeParams.filter.push({ field: 'category', value: this.categoriesControl.value.join(',') });
      }
      this.loadData();
    });
  }

  loadData() {
    this.service.getPage(this.routeParams).subscribe((res) => {
      this.productsPage = res;
    });
  }

  ngOnInit(): void {
    this.loadData();

    this.categoriesControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.routeParams.filter = value ? [{ field: 'category', value: value.join(',') }] : [];
      if (this.searchControl.value) {
        this.routeParams.filter.push({ field: 'search', value: this.searchControl.value });
      }
      this.loadData();
    });
  }

  deleteProductConfirmed() {
    if (!this.productToDelete) {
      return;
    }
    this.service.delete(this.productToDelete.id + '').subscribe({
      next: () => {
        this.productToDelete = undefined;
        this.loadData();
      },
      error: (err) => {
        this.productToDelete = undefined;
        this.productDeleteError = err.error.message;
      },
    });
  }

  handleRoute(sorts: RouteParams) {
    this.routeParams.sort = sorts.sort;
    this.routeParams.pageNum = sorts.pageNum;
    this.routeParams.pageSize = sorts.pageSize;
    this.loadData();
  }
}
